/**
 * Closed preset sets, and the ratios every dimension is derived from.
 *
 * Named presets, never free-form values: a closed set still enforces one look
 * across many authors, a slider does not.
 */

import { type Ratio, ratio } from "./units";

// --- Caption ratios (spec section 7). Nothing outside this block is a pixel. ---

/** Of source height. 65px at 1080x1920, 26 characters per line. */
export const FONTSIZE = ratio(0.034);
export const PADDING_X = ratio(0.00547);
export const PADDING_Y = ratio(0.0016);
export const STROKE = ratio(0.003);
/** Of source width. Set by TikTok's right-hand engagement rail at 11.1%. */
export const SAFE_WIDTH = ratio(0.72);
/** Of Fontsize. A caption box is exactly one line advance tall, so boxes tile. */
export const LINE_ADVANCE = ratio(1.09);
/** Of the font's own space: a full space is too loose at caption size. */
export const WORD_GAP = ratio(0.85);

export class ColourError extends Error {}

/** #RRGGBB to ASS's &HAABBGGRR, which is byte-reversed. */
export function assColour(hexRgb: string): string {
  let value = hexRgb;
  while (value.startsWith("#")) value = value.slice(1);
  if ([...value].length !== 6) throw new ColourError(`expected #RRGGBB, got '${hexRgb}'`);
  const red = value.slice(0, 2).toUpperCase();
  const green = value.slice(2, 4).toUpperCase();
  const blue = value.slice(4, 6).toUpperCase();
  return `&H00${blue}${green}${red}`;
}

/**
 * The faces an operator may choose. JetBrains Mono is the default and the only
 * monospace one; the rest are proportional, which works because text is
 * measured glyph by glyph.
 */
export const FONTS: Readonly<Record<string, string>> = {
  "JetBrains Mono": "JetBrainsMono-Bold.ttf",
  Montserrat: "Montserrat-Bold.ttf",
  Manrope: "Manrope-Bold.ttf",
  Onest: "OnestBold1602-hint.ttf",
  Unbounded: "Unbounded-Bold.ttf",
  "PT Serif": "PTSerif-Bold.ttf",
};

export const DEFAULT_FONT = "JetBrains Mono";

/** The accent on a dark ground: brighter than the interface accent, so it holds through compression. */
export const ACCENT_ON_DARK = "#568FFF";
/** The same hue darkened for `invert`, whose ground is white. */
export const ACCENT_ON_LIGHT = "#2F6BE0";

export interface Style {
  readonly name: string;
  readonly text: string;
  readonly box: string | null;
  readonly accent: string;
  readonly stroked: boolean;
}

export const STYLES: Readonly<Record<string, Style>> = {
  slab: {
    name: "slab",
    text: assColour("#FFFFFF"),
    box: assColour("#000000"),
    accent: assColour(ACCENT_ON_DARK),
    stroked: false,
  },
  invert: {
    name: "invert",
    text: assColour("#000000"),
    box: assColour("#FFFFFF"),
    accent: assColour(ACCENT_ON_LIGHT),
    stroked: false,
  },
  // The only preset whose legibility depends on the footage behind it.
  stroke: {
    name: "stroke",
    text: assColour("#FFFFFF"),
    box: null,
    accent: assColour(ACCENT_ON_DARK),
    stroked: true,
  },
};

export interface Position {
  readonly name: string;
  readonly bottom: Ratio;
  readonly clears: readonly string[];
}

// These names must match safezone.DEAD_ZONES exactly: they are what lets a
// preset's claim be checked against a measurement rather than merely asserted.
const SHORT_FORM = ["YouTube Shorts", "Shorts expanded", "TikTok", "TikTok ads"];
const EVERYTHING = [...SHORT_FORM, "Reels"];

export const POSITIONS: Readonly<Record<string, Position>> = {
  low: { name: "low", bottom: ratio(0.22), clears: SHORT_FORM },
  mid: { name: "mid", bottom: ratio(0.3), clears: SHORT_FORM },
  safe: { name: "safe", bottom: ratio(0.38), clears: EVERYTHING },
  high: { name: "high", bottom: ratio(0.45), clears: EVERYTHING },
};


/** Channel identities, including the caption dimensions recorded with their designs. */
export const CAPTION_DESIGN_NAMES = ["servarium", "enshrouded", "runescape", "valheim"] as const;
export type CaptionDesignName = typeof CAPTION_DESIGN_NAMES[number];

export interface CaptionDesign {
  readonly label: string;
  readonly channel: string;
  readonly font: string;
  readonly style: string;
  readonly position: string;
  readonly accent: string;
  readonly text: string;
  readonly outline: string;
  readonly stroke: Ratio;
  readonly lineAdvance: Ratio;
  readonly wordGap: Ratio;
  readonly gapHeight: Ratio | null;
}

export const CAPTION_DESIGNS: Readonly<Record<CaptionDesignName, CaptionDesign>> = {
  servarium: {
    label: "Servarium", channel: "Servarium", font: DEFAULT_FONT,
    style: "stroke", position: "low", accent: ACCENT_ON_DARK, text: "#FFFFFF",
    outline: "#000000", stroke: STROKE, lineAdvance: LINE_ADVANCE,
    wordGap: WORD_GAP, gapHeight: null,
  },
  enshrouded: {
    label: "Enshrouded", channel: "Эншраудес", font: "Manrope",
    style: "stroke", position: "safe", accent: "#B7CA91", text: "#FFF1DA",
    outline: "#4B3527", stroke: ratio(0.0016), lineAdvance: ratio(1),
    wordGap: WORD_GAP, gapHeight: ratio(0.0042),
  },
  runescape: {
    label: "RuneScape", channel: "Рунописец", font: "PT Serif",
    style: "stroke", position: "mid", accent: "#D8B66A", text: "#FFFFFF",
    outline: "#4A2E1B", stroke: STROKE, lineAdvance: LINE_ADVANCE,
    wordGap: WORD_GAP, gapHeight: null,
  },
  valheim: {
    label: "Valheim", channel: "Локс и хаос", font: "Unbounded",
    style: "stroke", position: "low", accent: "#FFD12A", text: "#FFFFFF",
    outline: "#102A33", stroke: STROKE, lineAdvance: LINE_ADVANCE,
    wordGap: ratio(1.65), gapHeight: null,
  },
};

/** Boxed white captions retain their dark text when a channel is selected. */
export function captionStyle(style: string, design: CaptionDesign | null): Style {
  const base = STYLES[style];
  if (!base) throw new RangeError(`unknown style '${style}'`);
  return design && style !== "invert" ? { ...base, text: assColour(design.text) } : base;
}
